import { Key, useCallback } from 'react';
import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { default as useAcquireToken } from "../../AuthToken/Hooks/useAcquireToken";
import { default as useBSTInsightsAPI } from "../../../apiclients/api/bstinsightsapi";
import { UserDTO, InvitationStrategy } from "../../../apiclients/bstinsights";
import { isUserInvitable } from './utils/isUserInvitable';
import { SENDING_INVITATION_STATUS } from './utils/constants';
import { useGetUsersPagedQueryKey } from './useGetUsersPaged';
import { useGridDataQueryInvalidate } from './useGridDataQueryInvalidate';

type Payload = {
  users: UserDTO[];
  selected: Key[]
  invitationStrategy: InvitationStrategy;
}

/* istanbul ignore next */
const useOptimistUpdate = () => {
  const queryClient = useQueryClient()
  const gridQueryKey = useGetUsersPagedQueryKey()

  return useCallback(async ({ selected, invitationStrategy }: Payload) => {
    await queryClient.cancelQueries({ queryKey: gridQueryKey })

    const previousData = queryClient.getQueryData(gridQueryKey) as InfiniteData<UserDTO[], unknown> | undefined

    // Update the cache optimistically
    queryClient.setQueryData(gridQueryKey, (prevData: InfiniteData<UserDTO[], unknown> | undefined) => {
      if (prevData) {
        return {
          ...prevData,
          pages: prevData.pages.map((page) => {
            // Select All Checked
            if (invitationStrategy === 1) {
              return page.map((user) => {
                const userExcluded = selected.includes(user.userId!)
                const userCanBeInvited = isUserInvitable(user)
                // Change status of invitable user
                if (userCanBeInvited && !userExcluded) {
                  return {
                    ...user,
                    accountStatusName: SENDING_INVITATION_STATUS
                  }
                }
                // Skip others
                return user;
              });
            }
            return page.map((user) => {
              // Update selected user
              if (selected.includes(user.userId!)) {
                return {
                  ...user,
                  accountStatusName: SENDING_INVITATION_STATUS
                }
              }
              return user;
            })
          })
        }
      }
    })
    return { previousData }
  }, [gridQueryKey, queryClient])
}

export const useInviteUser = () => {
  const queryClient = useQueryClient()
  const gridQueryKey = useGetUsersPagedQueryKey()
  const { getTokenSilently } = useAcquireToken();
  const { inviteUsersWithRoles } = useBSTInsightsAPI();
  const optimisticallyUpdateUser = useOptimistUpdate()
  const invalidateGridDataQueries = useGridDataQueryInvalidate()

  return useMutation({
    mutationFn: async ({
      users,
      invitationStrategy,
    }: Payload) => {
      const token = await getTokenSilently();
      if (!token) return;
      const data = users.map((user) => ({
        email: user.mail!,
        roleIds: user.groups,
      }));
      return await inviteUsersWithRoles(token, data, invitationStrategy);
    },
    onMutate: optimisticallyUpdateUser,
    onSettled: invalidateGridDataQueries,
    onError: (_, __, context) => queryClient.setQueryData(gridQueryKey, context?.previousData)
  });
};
